// src/components/login/password-requirements.tsx
import { Check } from "lucide-react";

/**
 * Mirrors backend app/models/auth_models.py _validate_password_strength.
 * Keep these in sync — they are the FinServ-aligned policy.
 */
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 128;

export interface PasswordChecks {
  length: boolean;
  upper: boolean;
  lower: boolean;
  digit: boolean;
  symbol: boolean;
  notTooLong: boolean;
  allValid: boolean;
}

export function checkPassword(pw: string): PasswordChecks {
  const length    = pw.length >= PASSWORD_MIN_LENGTH;
  const notTooLong = pw.length <= PASSWORD_MAX_LENGTH;
  const upper     = /[A-Z]/.test(pw);
  const lower     = /[a-z]/.test(pw);
  const digit     = /\d/.test(pw);
  const symbol    = /[^A-Za-z0-9\s]/.test(pw);
  return {
    length, upper, lower, digit, symbol, notTooLong,
    allValid: length && notTooLong && upper && lower && digit && symbol,
  };
}

interface Props {
  password: string;
  /** Hide entirely when password is empty (default true) */
  hideWhenEmpty?: boolean;
}

export function PasswordRequirements({ password, hideWhenEmpty = true }: Props) {
  if (hideWhenEmpty && !password) return null;

  const c = checkPassword(password);
  const items: { met: boolean; text: string }[] = [
    { met: c.length,  text: `At least ${PASSWORD_MIN_LENGTH} characters` },
    { met: c.upper,   text: "One uppercase letter (A–Z)" },
    { met: c.lower,   text: "One lowercase letter (a–z)" },
    { met: c.digit,   text: "One number (0–9)" },
    { met: c.symbol,  text: "One symbol (! @ # $ % ^ & * etc.)" },
  ];

  return (
    <div>
      <div
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: "var(--text-xs)",
          color: "var(--muted-foreground)",
          marginBottom: 10,
          fontWeight: 500,
        }}
      >
        Password requirements:
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.map((it) => (
          <Row key={it.text} met={it.met} text={it.text} />
        ))}
      </div>
    </div>
  );
}

function Row({ met, text }: { met: boolean; text: string }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        fontFamily: "var(--font-sans)",
        fontSize: "var(--text-xs)",
      }}
    >
      <div
        style={{
          width: 20,
          height: 20,
          borderRadius: "50%",
          backgroundColor: met ? "var(--signal-green)" : "var(--muted)",
          border: met ? "none" : "1.5px solid var(--border)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          transition: "background-color 0.15s ease",
        }}
      >
        {met && (
          <Check style={{ width: 11, height: 11, color: "white", strokeWidth: 3 }} />
        )}
      </div>
      <span
        style={{
          color: met ? "var(--foreground)" : "var(--muted-foreground)",
          fontWeight: met ? 500 : 400,
        }}
      >
        {text}
      </span>
    </div>
  );
}